import { GAME_ASSET_SLUG } from "@/config/game";
import { CardEffects } from "@/design-system/PlayerCard/CardEffects";
import { extractColors } from "@/functions/extractCardColors";
import { EvolutionsIcon } from "@/icons";
import { cn } from "@/lib/utils";
import { contentLabelsSelector } from "@/redux/loadedContent/loadedContentSlice";
import { getVersionAssetsSelector } from "@/redux/loadedContent/loadedContentSlice";
import { useTranslation } from "@repo/shared/i18n";
import { CardFont, Foil } from "@repo/shared/types";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import Chemistry from "../../Chemistry/Chemistry";
import { PlayStylePlus } from "../PlayStylePlus/PlayStylePlus";

type Props = {
  rating: number;
  cardName: string;
  cardFont?: CardFont;
  foil?: Foil;
  preferredPosition: string;
  clubId: number;
  countryId: number;
  leagueId: number;
  versionId: number;
  playerUrl: string;
  metalId: number;
  hasDynamicImage: boolean;
  playStylesPlus?: number[];
  chemistry?: 0 | 1 | 2 | 3;
  isOnIncludeExcludeSection?: boolean;
  isEvolutions?: boolean;
  isEvolvedPlayer?: boolean;
};

const SmallPlayer = ({
  rating,
  cardName,
  cardFont,
  foil,
  preferredPosition,
  clubId,
  countryId,
  leagueId,
  versionId,
  playerUrl,
  metalId,
  hasDynamicImage,
  playStylesPlus,
  chemistry,
  isOnIncludeExcludeSection,
  isEvolutions,
  isEvolvedPlayer,
}: Props) => {
  const versionAssets = useSelector(getVersionAssetsSelector);
  const { positions } = useSelector(contentLabelsSelector);
  const { t } = useTranslation("common");
  const assetsBaseUrl = import.meta.env.VITE_APP_ASSETS_BASE_URL;
  const { topText, bottomText, cardUrl, isLight, strip } = extractColors({
    versionAssets,
    versionId,
    metalId,
  });
  const playerImage = (
    <img
      alt={cardName}
      className={cn(
        "absolute right-[8%]",
        hasDynamicImage && versionId !== 72
          ? "bottom-[12%] w-[84%] "
          : "bottom-[37%] w-[70%] ",
      )}
      src={playerUrl + "?v5"}
    />
  );
  const cardImage = (
    // eslint-disable-next-line i18next/no-literal-string -- asset id, not copy
    <img alt="type" className="w-24 aspect-95/133" src={cardUrl} />
  );

  const clubLeagueCountry = (
    <div className="absolute bottom-[15%] w-full flex flex-row justify-center gap-x-1 talbet:gap-x-2 items-center">
      <img
        alt={t("alt.country")}
        className="w-3 touch-device:w-4 aspect-104/62"
        src={`${assetsBaseUrl}/${GAME_ASSET_SLUG}/countries/${countryId}.png`}
      />
      <img
        alt={t("alt.league")}
        className="w-3 h-3 touch-device:w-4 touch-device:h-4"
        src={`${assetsBaseUrl}/${GAME_ASSET_SLUG}/leagues/${
          isLight ? "" : "dark/"
        }${leagueId}.png`}
      />
      <img
        alt={t("alt.club")}
        className="w-[10px] h-[10px] touch-device:w-3 touch-device:h-3"
        src={`${assetsBaseUrl}/${GAME_ASSET_SLUG}/clubs/${
          isLight ? "" : "dark/"
        }${clubId}.png`}
      />
    </div>
  );

  const playerImageAndCardImage = (
    <div className="relative">
      {/* Over the version art, under the player image. */}
      <CardEffects foil={foil} cardUrl={cardUrl} />
      {playerImage}
      {cardImage}
    </div>
  );

  const chemistrySection = (
    <div className="absolute bottom-1 left-1 w-fit">
      <Chemistry
        chemistry={chemistry || 0}
        previousChemistry={chemistry || 0}
        isOutOfPosition={false}
        backgroundColor="bg-gray-900"
      />
    </div>
  );

  const playStylePlusWrapper = (
    <div className="absolute top-[40%] left-[2px] flex flex-col">
      {playStylesPlus?.map((playStylePlusId, index) => (
        <div
          className={cn(
            "w-4 h-4",
            playStylesPlus && playStylesPlus.length > 3 && index > 0 && "-mt-1",
          )}
          key={playStylePlusId}
        >
          <PlayStylePlus
            id={playStylePlusId}
            primaryColor={bottomText}
            secondaryColor={strip}
          />
        </div>
      ))}
    </div>
  );

  const playerName = (
    <span
      className="absolute flex items-center justify-center inset-x-0 bottom-[25%] touch-device:bottom-[26%] text-tiny desktop:text-xs whitespace-nowrap"
      style={{
        fontFamily: cardFont === "jalliya" ? "JalliyaSeven" : "CruyffMedium",
        color: bottomText,
      }}
    >
      {cardName}
    </span>
  );

  const ratingAndPosition = (
    <div
      className="absolute top-[20%] left-[14%] flex flex-col"
      style={{
        fontFamily: "CruyffMedium",
        color: topText,
      }}
    >
      <span>{rating}</span>
      <span
        className="-mt-1.5 ml-0.5 text-[9px]"
        style={{
          fontFamily: "CruyffCondensedMedium",
        }}
      >
        {positions[preferredPosition]?.name ?? preferredPosition}
      </span>
    </div>
  );

  const evolutionsIcon = (
    <div className="absolute bottom-3 right-2 flex flex-col w-4 h-4">
      <EvolutionsIcon outlineColor={topText} backgroundColor={strip} />
    </div>
  );

  return (
    <article className={cn("relative flex items-center")}>
      {playerImageAndCardImage}
      {ratingAndPosition}
      {playerName}
      {!isOnIncludeExcludeSection &&
        playStylesPlus?.length > 0 &&
        playStylePlusWrapper}
      {clubLeagueCountry}
      {!isOnIncludeExcludeSection && chemistry === 3 && chemistrySection}
      {(isEvolutions || isEvolvedPlayer) && evolutionsIcon}
    </article>
  );
};

export default SmallPlayer;
